import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Stage, Lead, Pipeline } from "../../pages/Crm/utils/types";

export interface CrmViewItem {
  id: string;
  name: string;
  emoji_icon?: string;
  scope?: string;
  filter_groups?: unknown[];
  logical_operator?: string;
  created_at?: string;
  updated_at?: string;
}

interface CrmLeadListState {
  stages: { [key: string]: Stage };
  pipelines: Pipeline[];
  activeTab: string;
  activeView: "grid" | "list";
  selectedViewId: string;
  newlyCreatedViewId: string | null;
  crmViews: CrmViewItem[];
  viewRefreshTrigger: number;
  collapsedStageIds: string[];
  autoCollapseSignature: string;
  stagesLoadedSignature: string;
}

const initialState: CrmLeadListState = {
  stages: {},
  pipelines: [],
  activeTab: "all",
  activeView: "grid",
  selectedViewId: "all_leads",
  newlyCreatedViewId: null,
  crmViews: [],
  viewRefreshTrigger: 0,
  collapsedStageIds: [],
  autoCollapseSignature: "",
  stagesLoadedSignature: "",
};

const crmLeadListSlice = createSlice({
  name: "crmLeadList",
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setPipelines: (state, action: PayloadAction<Pipeline[]>) => {
      state.pipelines = action.payload;
    },
    setActiveView: (state, action: PayloadAction<"grid" | "list">) => {
      state.activeView = action.payload;
    },
    setStages: (
      state,
      action: PayloadAction<{
        [k: string]: Stage;
      }>
    ) => {
      state.stages = action.payload;
    },
    appendStageLeads: (
      state,
      action: PayloadAction<{
        stageId: string;
        leads: Lead[];
        newSkip: number;
        count?: number;
      }>
    ) => {
      const { stageId, leads, newSkip, count } = action.payload;
      if (state.stages[stageId]) {
        state.stages[stageId].leads = [
          ...state.stages[stageId].leads,
          ...leads,
        ];
        state.stages[stageId].skip = newSkip;
        if (count !== undefined) {
          state.stages[stageId].count = count;
        }
      }
    },
    prependLeadToStage: (
      state,
      action: PayloadAction<{
        stageId: string;
        lead: Lead;
      }>
    ) => {
      const { stageId, lead } = action.payload;
      if (state.stages[stageId]) {
        state.stages[stageId].leads = [lead, ...state.stages[stageId].leads];
        state.stages[stageId].count = state.stages[stageId].count + 1;
      }
    },
    updateLeadInStage: (
      state,
      action: PayloadAction<{
        stageId: string;
        lead: Lead;
      }>
    ) => {
      const { stageId, lead } = action.payload;
      if (state.stages[stageId]) {
        state.stages[stageId].leads = state.stages[stageId].leads.map((l) =>
          l.lead_id === lead.lead_id ? lead : l
        );
      }
    },
    updateStageFilters: (
      state,
      action: PayloadAction<{
        stageId: string;
        filters: Stage["filters"];
        leads: Lead[];
        count: number;
      }>
    ) => {
      const { stageId, filters, leads, count } = action.payload;
      if (state.stages[stageId]) {
        state.stages[stageId].filters = filters;
        state.stages[stageId].leads = leads;
        state.stages[stageId].count = count;
        state.stages[stageId].skip = 10;
      }
    },
    setSelectedViewId: (state, action: PayloadAction<string>) => {
      state.selectedViewId = action.payload;
    },
    setNewlyCreatedViewId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.newlyCreatedViewId = action.payload;
    },
    setCrmViews: (state, action: PayloadAction<CrmViewItem[]>) => {
      state.crmViews = action.payload;
    },
    addCrmView: (state, action: PayloadAction<CrmViewItem>) => {
      state.crmViews.push(action.payload);
    },
    updateCrmView: (state, action: PayloadAction<CrmViewItem>) => {
      const idx = state.crmViews.findIndex(
        (v) => v.id === action.payload.id
      );
      if (idx !== -1) state.crmViews[idx] = action.payload;
      if (state.selectedViewId === action.payload.id) {
        state.viewRefreshTrigger += 1;
      }
    },
    removeCrmView: (state, action: PayloadAction<string>) => {
      state.crmViews = state.crmViews.filter(
        (v) => v.id !== action.payload
      );
      if (state.selectedViewId === action.payload) {
        state.selectedViewId = "all_leads";
      }
    },
    resetStages: (state) => {
      state.stages = {};
      state.pipelines = [];
    },
    setCollapsedStageIds: (state, action: PayloadAction<string[]>) => {
      state.collapsedStageIds = action.payload;
    },
    toggleCollapsedStage: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      if (!id) return;
      const idx = state.collapsedStageIds.indexOf(id);
      if (idx === -1) {
        state.collapsedStageIds.push(id);
      } else {
        state.collapsedStageIds.splice(idx, 1);
      }
    },
    clearCollapsedStages: (state) => {
      state.collapsedStageIds = [];
    },
    setAutoCollapseSignature: (state, action: PayloadAction<string>) => {
      state.autoCollapseSignature = action.payload;
    },
    setStagesLoadedSignature: (state, action: PayloadAction<string>) => {
      state.stagesLoadedSignature = action.payload;
    },
    moveLeadBetweenStages: (
      state,
      action: PayloadAction<{
        leadId: string;
        fromStageId: string;
        toStageId: string;
      }>
    ) => {
      const { leadId, fromStageId, toStageId } = action.payload;
      
      // Find and remove lead from source stage
      const fromStage = state.stages[fromStageId];
      const toStage = state.stages[toStageId];
      
      if (fromStage && toStage) {
        const leadIndex = fromStage.leads.findIndex(
          (l) => l.lead_id === leadId
        );
        
        if (leadIndex !== -1) {
          const [movedLead] = fromStage.leads.splice(leadIndex, 1);
          // Update the lead's current_stage
          movedLead.current_stage = toStageId;
          // Add to target stage at the beginning
          toStage.leads.unshift(movedLead);
          // Update counts
          fromStage.count = Math.max(0, fromStage.count - 1);
          toStage.count = toStage.count + 1;
          fromStage.skip = fromStage.leads.length;
          toStage.skip = toStage.leads.length;
        }
      }
    },
  },
});

export const {
  setActiveTab,
  setActiveView,
  setPipelines,
  setSelectedViewId,
  setNewlyCreatedViewId,
  setCrmViews,
  addCrmView,
  updateCrmView,
  removeCrmView,
  setStages,
  appendStageLeads,
  prependLeadToStage,
  updateLeadInStage,
  updateStageFilters,
  resetStages,
  moveLeadBetweenStages,
  setCollapsedStageIds,
  toggleCollapsedStage,
  clearCollapsedStages,
  setAutoCollapseSignature,
  setStagesLoadedSignature,
} = crmLeadListSlice.actions;

export default crmLeadListSlice.reducer;
